/* eslint-disable @nx/enforce-module-boundaries */
import { DIALOG_DATA } from "@angular/cdk/dialog";
import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, inject, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { PopupDto, PopupService } from "@malirang/api-client";
import {
    fnGetKeyByValue,
    NO_SEE_TODAY_ADVERTISE_POPUP,
    NO_SEE_TODAY_NORMAL_POPUP,
    PopupType,
} from "@malirang/common";
import {
    LocalStorageService,
    ModalService,
    SwiperDirective,
    ToastService,
} from "@malirang/front-share";
import dayjs from "dayjs";
import { PopupControllerGetPopupSettingByType$Params } from "libs/api-client/src/lib/fn/popup/popup-controller-get-popup-setting-by-type";
import { SwiperOptions } from "swiper/types";

@Component({
    selector: "app-popup-setting-form",
    imports: [CommonModule, SwiperDirective],
    templateUrl: "./popup.component.html",
    styleUrl: "./popup.component.scss",
})
export default class PopupComponent implements AfterViewInit {
    readonly popupService = inject(PopupService);
    readonly toastService = inject(ToastService);
    readonly modalService = inject(ModalService);
    readonly localStorageService = inject(LocalStorageService);
    readonly dialogData = inject(DIALOG_DATA);

    baseSwiperOptions: SwiperOptions = {
        slidesPerView: 1,
        centeredSlides: true,
        allowTouchMove: true,
        spaceBetween: 16,
        loop: false,
        autoplay: {
            delay: 5000,
        },
    };

    normalPopupSetting$ = rxResource({
        stream: () =>
            this.popupService.popupControllerGetPopupSettingByType({
                type: fnGetKeyByValue(PopupType, PopupType.NORMAL),
            } as PopupControllerGetPopupSettingByType$Params),
    });

    adPopupSetting$ = rxResource({
        stream: () =>
            this.popupService.popupControllerGetPopupSettingByType({
                type: fnGetKeyByValue(PopupType, PopupType.ADVERTISE),
            } as PopupControllerGetPopupSettingByType$Params),
    });

    /**
     * @description 팝업
     */
    normalPopups = signal<PopupDto[]>(this.dialogData.normalPopups);
    adPopups = signal<PopupDto[]>(this.dialogData.adPopups);

    /**
     * @description 팝업 인덱스
     */
    normalRealIndex = signal<number>(0);
    adRealIndex = signal<number>(0);

    /**
     * @description 팝업 스와이퍼 옵션
     */
    normalSwiperOptions = signal<SwiperOptions>({});
    adSwiperOptions = signal<SwiperOptions>({});

    /**
     * @description 팝업 알림 여부
     */
    isNormalPopupOpen = signal<boolean>(false);
    isAdPopupOpen = signal<boolean>(false);

    /**
     * @description 팝업 위치 조정 가능 여부 및 설정
     */
    adClass = signal<string>("");
    adTop = signal<number | undefined>(undefined);
    adLeft = signal<number | undefined>(undefined);
    normalClass = signal<string>("");
    normalTop = signal<number | undefined>(undefined);
    normalLeft = signal<number | undefined>(undefined);

    isLoading = signal<boolean>(true);

    constructor() {
        // 스와이퍼 롤링 속도
        this.adSwiperOptions.set({
            ...this.baseSwiperOptions,
            autoplay: {
                delay: this.adPopupSetting$?.value()?.rollingSpeed || 5000,
            },
        });

        this.normalSwiperOptions.set({
            ...this.baseSwiperOptions,
            autoplay: {
                delay: this.normalPopupSetting$?.value()?.rollingSpeed || 5000,
            },
        });

        // 광고 팝업 위치 조정
        if (this.dialogData.adPopups && this.dialogData.adPopups?.length > 0) {
            this.isAdPopupOpen.set(true);
            if (
                !Capacitor.isNativePlatform() &&
                this.normalPopupSetting$?.value()?.isHandlePosition
            ) {
                this.adClass.set("absolute");
                this.adTop.set(this.adPopupSetting$?.value()?.positionTop || 0);
                this.adLeft.set(
                    this.adPopupSetting$?.value()?.positionLeft || 0,
                );
            }
        }

        // 일반 팝업 위치 조정
        if (
            this.dialogData.normalPopups &&
            this.dialogData.normalPopups?.length > 0
        ) {
            this.isNormalPopupOpen.set(true);
            if (
                !Capacitor.isNativePlatform() &&
                this.normalPopupSetting$?.value()?.isHandlePosition
            ) {
                this.normalClass.set("absolute");
                this.normalTop.set(
                    this.normalPopupSetting$?.value()?.positionTop || 0,
                );
                this.normalLeft.set(
                    this.normalPopupSetting$?.value()?.positionLeft || 0,
                );
            }
        }

        const adHasManualPosition =
            this.adPopupSetting$?.value()?.isHandlePosition;
        const normalHasManualPosition =
            this.normalPopupSetting$?.value()?.isHandlePosition;

        if (adHasManualPosition && !normalHasManualPosition)
            this.normalClass.set("relative mx-auto");

        if (!adHasManualPosition && normalHasManualPosition)
            this.adClass.set("relative mx-auto");
    }

    ngAfterViewInit(): void {
        this.isLoading.set(false);
    }

    /**
     * @name handleNotToday
     * @description 팝업 오늘하루 그만보기
     * @param {keyof typeof PopupType} type
     * @param {Event} ev
     */
    async handleNotToday(
        type: keyof typeof PopupType,
        ev?: Event,
    ): Promise<void> {
        ev?.stopPropagation();
        ev?.preventDefault();

        const todayEnd = dayjs().endOf("day").toDate()?.toString();
        const key =
            type === fnGetKeyByValue(PopupType, PopupType.ADVERTISE)
                ? NO_SEE_TODAY_ADVERTISE_POPUP
                : NO_SEE_TODAY_NORMAL_POPUP;

        await this.localStorageService.set(key, todayEnd);
        try {
            if (Capacitor.isNativePlatform()) {
                await Preferences?.set({
                    key,
                    value: todayEnd,
                });
            }
        } catch (error) {
            console.error("Preferences set error:", error);
        }

        this.close(type);
        return;
    }

    /**
     * @name close
     * @description 팝업 닫기
     * @param {keyof typeof PopupType} type
     * @param {Event} ev
     */
    close(type: keyof typeof PopupType, ev?: Event): void {
        ev?.stopPropagation();
        ev?.preventDefault();
        if (type === fnGetKeyByValue(PopupType, PopupType.ADVERTISE))
            this.isAdPopupOpen.set(false);

        if (type === fnGetKeyByValue(PopupType, PopupType.NORMAL))
            this.isNormalPopupOpen.set(false);

        if (!this.isAdPopupOpen() && !this.isNormalPopupOpen())
            this.modalService.close();
    }
}
